// src/components/sidebar.js — sidebar de navegação principal V2.
// Estrutura: grupos colapsáveis com sub-itens, logo Angular real, user area.

import { tenant } from '../context/tenantContext.js';
import { comprimirImagem } from '../lib/imageCompress.js';
import { maybeStartOnboarding } from '../lib/onboarding.js';
import { sb } from '../api/supabaseClient.js';
import { signOut } from '../context/authContext.js';
import { navigate, getCurrentRoute } from '../router/router.js';
import { Icons } from '../lib/icons.js';
import { esc } from '../lib/dom.js';
import { showToast } from '../lib/toast.js';
import './sidebar.css';
import { mountTopbar } from './topbar.js';
import { filterNavGroups } from '../lib/permissions.js';

// ─── Grupos colapsáveis · admin desktop ─────────────────────────────
// Cada grupo tem cor brand, ícone e sub-items.
// items[].soon=true → mostra toast "em migração" sem navegar.
export const NAV_GROUPS_ADMIN = [   // exportado p/ lib/ilhaNav.js (04/07)
  {
    id: 'inteligencia', color: '#7C5CFC', icon: 'dashboard', label: 'Inteligência',
    items: [
      { page: 'dashboard', icon: 'dashboard', label: 'Dashboard Operação' },
      { page: 'bi',        icon: 'bi',        label: 'Dashboard Financeiro' },
      { page: 'rh',        icon: 'pulso',     label: 'Painel RH' },
      { page: 'mapa',      icon: 'mapa',      label: 'Mapa de Obras' },
      { page: 'assistente', icon: 'bolt',     label: 'Assistente IA' }, // feature 003 · CFO de Bolso
    ],
  },
  {
    id: 'operacoes', color: '#EC4899', icon: 'obra', label: 'Operações',
    items: [
      { page: 'obras',           icon: 'obra',       label: 'Obras' },
      { page: 'clientes',        icon: 'cliente',    label: 'Clientes' },
      { page: 'etapas',          icon: 'grafico',    label: 'Progresso' },
      { page: 'tarefas',         icon: 'tarefa',     label: 'Planejamento' },
      { page: 'cronograma',      icon: 'calendario', label: 'Cronograma' },
      { page: 'obras_entregues', icon: 'casa',       label: 'Obras Entregues' },
      { page: 'garantias',       icon: 'escudo',     label: 'Garantias' },
      { page: 'portfolio',       icon: 'imagem',     label: 'Portfólio' },
    ],
  },
  {
    id: 'financeiro', color: '#C99B3D', icon: 'saldo', label: 'Financeiro',
    items: [
      { page: 'contas_receber', icon: 'receber',   label: 'Contas a Receber' },
      { page: 'contas_pagar',   icon: 'pagar',     label: 'Contas a Pagar' },
      { page: 'dre',            icon: 'pizza',     label: 'DRE Mensal' },
      { page: 'inadimplentes',  icon: 'warn',      label: 'Inadimplentes' },
      { page: 'recorrencias',   icon: 'refresh',   label: 'Recorrências' },
      { page: 'financeiro',     icon: 'saldo',     label: 'Lançamentos' },
      { page: 'despesas',       icon: 'cartao',    label: 'Despesas' },
      { page: 'reembolsos',     icon: 'reembolso', label: 'Reembolsos' },
      { page: 'orcamentos',     icon: 'prancheta', label: 'Orçamentos' },
      { page: 'compras',        icon: 'carrinho',  label: 'Compras' },
      { page: 'nfse_tomadas',   icon: 'bolt',      label: 'Notas Inteligentes' },
      { page: 'notas',          icon: 'recibo',    label: 'Notas Fiscais' },
    ],
  },
  {
    id: 'equipe', color: '#6D28D9', icon: 'pessoas', label: 'Equipe',
    items: [
      { page: 'funcionarios',      icon: 'pessoas',   label: 'Funcionários' },
      { page: 'terceirizados',     icon: 'userPlus',  label: 'Terceirizados' },
      { page: 'ponto',             icon: 'relogio',   label: 'Ponto Diário' },
      { page: 'espelho_ponto',     icon: 'rel',       label: 'Espelho de Ponto' },
      { page: 'horas',             icon: 'ampulheta', label: 'Banco de Horas' },
      { page: 'docs_funcionarios', icon: 'pasta',     label: 'Documentos & NR' },
      { page: 'ausencias',         icon: 'userX',     label: 'Afastamentos' },
      { page: 'ferias_rh',         icon: 'sol',       label: 'Férias' },
    ],
  },
  {
    id: 'comercial', color: '#FB923C', icon: 'responsavel', label: 'Comercial',
    items: [
      { page: 'contratos',    icon: 'contrato',    label: 'Contratos' },
      { page: 'fornecedores', icon: 'caminhao',    label: 'Fornecedores' },
      { page: 'arquivos',     icon: 'arquivo',     label: 'Arquivos & Diário' },
    ],
  },
  {
    id: 'atividades', color: 'var(--sc-info)', icon: 'rel', label: 'Atividades',
    items: [
      { page: 'relatorios',    icon: 'rel',   label: 'Relatórios' },
      { page: 'atividades',    icon: 'lista', label: 'Log de Atividades' },
      { page: 'meus_chamados', icon: 'boia',  label: 'Meus Chamados' },
    ],
  },
  {
    id: 'sistema', color: 'var(--sc-idle)', icon: 'config', label: 'Sistema',
    items: [
      { page: 'configuracoes', icon: 'config', label: 'Configurações' },
      { page: 'usuarios',      icon: 'chave',  label: 'Usuários' },
      { page: 'upgrade',       icon: 'plano',  label: 'Planos' },
      { page: 'sobre',         icon: 'info',   label: 'Sobre' },
    ],
  },
];

// Estado dos grupos abertos · persistido em localStorage
const LS_KEY = 'sidebar-groups-open';
function _loadOpen() {
  try { return new Set(JSON.parse(localStorage.getItem(LS_KEY) || '[]')); } catch { return new Set(); }
}
function _saveOpen(set) {
  try { localStorage.setItem(LS_KEY, JSON.stringify([...set])); } catch {}
}
let _openGroups = _loadOpen();

// Garante que grupo do current page abre por default
function _autoOpenForRoute() {
  const { page } = getCurrentRoute();
  for (const g of NAV_GROUPS_ADMIN) {
    if (g.items.some((it) => it.page === page)) {
      _openGroups.add(g.id);
    }
  }
  _saveOpen(_openGroups);
}

let _installed = false;

// LOTE 0 (08/07): shell shadcn assume o desktop. Legado abaixo preservado p/ rollback
// rápido (trocar a const). Remoção definitiva no Lote 6.
const SC_SHELL = true;

export function mountShell() {
  if (SC_SHELL) { import('./shellShadcn.js').then((m) => m.mountShellShadcn()); return; }
  if (_installed) {
    updateActive();
    return;
  }
  _autoOpenForRoute();

  const root = document.getElementById('app-root');
  root.innerHTML = `
    <div class="app-shell">
      <aside class="app-sidebar" id="app-sidebar">
        ${_renderSidebar()}
      </aside>
      <div class="app-main-wrap">
        <div id="sub-bar" class="sub-bar"></div>
        <main id="main-content" class="app-main"></main>
      </div>
    </div>
  `;

  _bindListeners();
  try { mountTopbar(); } catch (e) { console.warn('[shell] topbar:', e); }
  try { _sbSetupCollapse(); } catch (e) { console.warn('[shell] collapse:', e); }
  _installed = true;
  maybeStartOnboarding();
}


// ─── Sidebar colapsável (rail + flyout) · opcional ──────────────────
function _sbPref(k, def) { try { const v = localStorage.getItem(k); return v === null ? def : v; } catch { return def; } }
function _sbFeatureOn() { return _sbPref('sb-feature', '1') === '1'; }

function _sbApplyCollapsed(collapsed, persist = true) {
  const shell = document.querySelector('.app-shell');
  if (!shell) return;
  shell.classList.toggle('sb-collapsed', !!collapsed);
  // largura via inline (mudança inline dispara a transição de forma confiável;
  // mudar via classe CSS deixava a transition "grudada" no Chrome neste layout)
  const sbEl = document.getElementById('app-sidebar');
  if (sbEl) {
    const mob = window.innerWidth <= 768;
    sbEl.style.width = collapsed ? (mob ? '50px' : '56px') : (mob ? '208px' : '240px');
  }
  const btn = document.getElementById('sb-collapse-btn');
  if (btn) btn.title = collapsed ? 'Expandir sidebar  [' : 'Recolher sidebar  [';
  if (persist) { try { localStorage.setItem('sb-collapsed', collapsed ? '1' : '0'); } catch {} }
}
function _sbToggleCollapse() {
  const shell = document.querySelector('.app-shell');
  if (!shell) return;
  _sbApplyCollapsed(!shell.classList.contains('sb-collapsed'));
  _sbResetAutoTimer();
}

let _sbAutoTimer = null;
function _sbResetAutoTimer() {
  if (_sbAutoTimer) { clearTimeout(_sbAutoTimer); _sbAutoTimer = null; }
  if (!_sbFeatureOn() || _sbPref('sb-autocollapse', '0') !== '1') return;
  const secs = Math.max(5, Number(_sbPref('sb-autocollapse-secs', '20')) || 20);
  _sbAutoTimer = setTimeout(() => {
    const shell = document.querySelector('.app-shell');
    if (shell && !shell.classList.contains('sb-collapsed')) _sbApplyCollapsed(true);
  }, secs * 1000);
}

// Auto-recolher os GRUPOS (accordion) por inatividade — mantém só o do módulo atual aberto
let _sbGrpTimer = null;
function _sbResetGrpTimer() {
  if (_sbGrpTimer) { clearTimeout(_sbGrpTimer); _sbGrpTimer = null; }
  if (!_sbFeatureOn() || _sbPref('sb-grpcollapse', '0') !== '1') return;
  if (window.__obTourAtivo) return; // onboarding em curso — não recolher grupos
  const secs = Math.max(5, Number(_sbPref('sb-grpcollapse-secs', '15')) || 15);
  _sbGrpTimer = setTimeout(() => {
    const el = document.getElementById('app-sidebar');
    if (!el) return;
    el.querySelectorAll('.sb-group').forEach((g) => {
      if (!g.classList.contains('has-active')) {
        g.classList.remove('is-open');
        const id = g.dataset.group; if (id) _openGroups.delete(id);
      }
    });
    _saveOpen(_openGroups);
  }, secs * 1000);
}
function _sbResetTimers() { _sbResetAutoTimer(); _sbResetGrpTimer(); }

function _sbSetupCollapse() {
  const sb = document.getElementById('app-sidebar');
  if (!sb) return;
  // remove botão antigo se re-montar
  document.getElementById('sb-collapse-btn')?.remove();
  if (!_sbFeatureOn()) { _sbApplyCollapsed(false, false); return; }

  const btn = document.createElement('button');
  btn.id = 'sb-collapse-btn';
  btn.className = 'sb-collapse-btn';
  btn.setAttribute('aria-label', 'Recolher sidebar');
  btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>';
  btn.addEventListener('click', (e) => { e.stopPropagation(); _sbToggleCollapse(); });
  sb.appendChild(btn);

  // estado salvo (lembrar último)
  const remember = _sbPref('sb-remember', '1') === '1';
  _sbApplyCollapsed(remember && _sbPref('sb-collapsed', '0') === '1', false);

  // atalho [ alterna
  if (!window.__sbKeyHook) {
    window.__sbKeyHook = true;
    document.addEventListener('keydown', (e) => {
      if (e.key === '[' && !/input|textarea|select/i.test(document.activeElement?.tagName || '')) {
        if (_sbFeatureOn()) { e.preventDefault(); _sbToggleCollapse(); }
      }
    });
  }
  // auto-recolher por inatividade
  ['mousemove', 'keydown', 'click'].forEach((ev) => document.addEventListener(ev, _sbResetTimers, { passive: true }));
  _sbResetTimers();
}
// expõe pra Configurações reconfigurar ao salvar preferências
window._sbRefreshCollapse = _sbSetupCollapse;


// ─── Upload da foto do avatar (clique no círculo do perfil) ─────────
async function _sbUploadAvatar(file) {
  if (!file || !tenant.user?.id) return;
  if (!/^image\//.test(file.type)) { showToast('Selecione um arquivo de imagem.', 'error'); return; }
  if (file.size > 5 * 1024 * 1024) { showToast('Imagem muito grande (máx. 5MB).', 'error'); return; }
  showToast('Enviando foto…');
  try {
    file = await comprimirImagem(file);
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const path = `${tenant.user.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await sb.storage.from('avatars').upload(path, file, { upsert: true, cacheControl: '3600' });
    if (upErr) { showToast('Erro no upload: ' + upErr.message, 'error'); return; }
    const { data: pub } = sb.storage.from('avatars').getPublicUrl(path);
    const url = pub?.publicUrl;
    const { error } = await sb.from('users_profile').update({ avatar_url: url }).eq('id', tenant.user.id);
    if (error) { showToast('Erro ao salvar: ' + error.message, 'error'); return; }
    const avEl = document.querySelector('.sb-user-avatar');
    if (avEl) avEl.innerHTML = `<img src="${url}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%">`;
    try { if (tenant.profile) tenant.profile.avatar_url = url; } catch (_) {}
    showToast('Foto do perfil atualizada!');
  } catch (e) {
    showToast('Erro de conexão ao enviar foto.', 'error');
  }
}

export function unmountShell() {
  if (SC_SHELL) { import('./shellShadcn.js').then((x) => x.unmountShellShadcn()); }
  _installed = false;
  const root = document.getElementById('app-root');
  if (root) root.innerHTML = '';
}

export function updateActive() {
  if (SC_SHELL) { import('./shellShadcn.js').then((x) => x.updateActiveShadcn()); return; }
  const { page } = getCurrentRoute();
  document.querySelectorAll('.sb-sub-item').forEach((el) => {
    el.classList.toggle('is-active', el.dataset.page === page);
  });
  // Reflete current group highlight
  document.querySelectorAll('.sb-group').forEach((g) => {
    const hasActive = !!g.querySelector('.sb-sub-item.is-active');
    g.classList.toggle('has-active', hasActive);
  });
}

// ─── Render ──────────────────────────────────────────────────────────
function _renderSidebar() {
  const nomeUser = tenant.profile?.nome || 'Usuário';
  const empresaNome = tenant.config?.nome_fantasia || tenant.config?.nome || 'Workspace';
  const tipoLabel = _roleLabel(tenant.profile?.tipo_usuario);
  const tipo = tenant.profile?.tipo_usuario;

  // SaaS super_admin tem nav própria simples (direct links, sem grupos)
  if (tipo === 'super_admin') {
    return `
      <div class="sb-head">
        <a class="sb-brand" data-action="nav" data-page="saas_dashboard">
          <svg class="sb-brand-mark" viewBox="0 0 218 218" aria-hidden="true"><use href="#logo-angular-grad"/></svg>
          <div class="sb-brand-text">
            <strong>ANGULAR <span class="erp">ERP</span></strong>
            <span>Platform Admin</span>
          </div>
        </a>
      </div>
      <nav class="sb-nav">
        <div class="sb-section-label">Platform Admin</div>
        ${_renderSaasItems()}
      </nav>
      ${_renderUserArea(nomeUser, tipoLabel)}
    `;
  }

  const groups = filterNavGroups(NAV_GROUPS_ADMIN);
  const { page: current } = getCurrentRoute();

  return `
    <div class="sb-head">
      <a class="sb-brand" data-action="nav" data-page="dashboard">
        <svg class="sb-brand-mark" viewBox="0 0 218 218" aria-hidden="true"><use href="#logo-angular-grad"/></svg>
        <div class="sb-brand-text">
          <strong>ANGULAR <span class="erp">ERP</span></strong>
        </div>
      </a>
    </div>
    <nav class="sb-nav">
      ${groups.map((g) => _renderGroup(g, current)).join('')}
    </nav>
    ${_renderUserArea(nomeUser, tipoLabel)}
  `;
}

function _renderGroup(g, current) {
  const isOpen = _openGroups.has(g.id);
  const hasActive = g.items.some((i) => i.page === current);
  return `
    <div class="sb-group ${isOpen ? 'is-open' : ''} ${hasActive ? 'has-active' : ''}" data-group="${g.id}">
      <button class="sb-group-btn" data-action="toggle-group" data-group="${g.id}">
        <span class="sb-group-icon" style="color:${g.color}">${Icons[g.icon]?.('sm') || ''}</span>
        <span class="sb-group-label">${esc(g.label)}</span>
        <span class="sb-group-chev">›</span>
      </button>
      <div class="sb-sub" data-grp="${esc(g.label)}">
        ${g.items.map((i) => {
          const isActive = i.page === current;
          return `
            <button class="sb-sub-item ${isActive ? 'is-active' : ''}"
                    data-action="nav" data-page="${i.page}" ${i.soon ? 'data-soon="1"' : ''}
                    style="${isActive ? `--accent:${g.color};--accent-bg:${g.color}1F` : ''}">
              <span class="sb-sub-icon" style="color:${isActive ? g.color : 'inherit'}">${Icons[i.icon]?.('sm') || ''}</span>
              <span>${esc(i.label)}</span>
            </button>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

function _renderSaasItems() {
  const items = [
    { page: 'saas_dashboard', icon: 'dashboard', label: 'Dashboard', color: '#C99B3D' },
    { page: 'saas_empresas',  icon: 'cliente',   label: 'Empresas',  color: '#FACC15' },
    { page: 'saas_saude',     icon: 'bi',        label: 'Saúde',     color: 'var(--sc-info)' },
  ];
  const { page: current } = getCurrentRoute();
  return items.map((it) => `
    <button class="sb-direct ${it.page === current ? 'is-active' : ''}"
            data-action="nav" data-page="${it.page}">
      <span style="color:${it.color}">${Icons[it.icon]?.('sm') || ''}</span>
      <span>${esc(it.label)}</span>
    </button>
  `).join('');
}

function _renderUserArea(nomeUser, tipoLabel) {
  const initial = (nomeUser || '?').trim().charAt(0).toUpperCase();
  const userId = tenant.user?.id || nomeUser;
  // Cor determinística do avatar
  let h = 0; for (const c of String(userId)) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const colors = ['#7C5CFC', '#6D28D9', '#EC4899', '#C99B3D', '#FB923C', '#FACC15'];
  const bg = colors[h % colors.length];
  // Logo + nome da empresa (white-label · mesmo logo dos PDFs)
  const cfg = tenant.config || {};
  const empNome = cfg.nome_fantasia || cfg.nome || '';
  const logoSvg = (cfg.logo_svg && String(cfg.logo_svg).trim().startsWith('<svg')) ? cfg.logo_svg : '';
  const logoUrl = cfg.logo_url || '';
  const empLogo = logoSvg
    ? logoSvg
    : (logoUrl ? `<img src="${esc(logoUrl)}" alt="">` : `<svg viewBox="0 0 218 218" aria-hidden="true"><use href="#logo-angular-grad"/></svg>`);
  const empresaHTML = empNome
    ? `<div class="sb-empresa"><div class="sb-empresa-logo">${empLogo}</div><div class="sb-empresa-nome">${esc(empNome)}</div></div>`
    : '';
  return `
    <div class="sb-foot">
      ${empresaHTML}
      <div class="sb-user">
        <div class="sb-user-avatar online" data-action="avatar" title="Trocar foto do perfil" style="background:${bg};cursor:pointer">${tenant.profile?.avatar_url ? `<img src="${esc(tenant.profile.avatar_url)}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%">` : esc(initial)}</div>
        <div class="sb-user-info">
          <strong>${esc(nomeUser)}</strong>
          <span>${esc(tipoLabel)}</span>
        </div>
        <button class="sb-signout" data-action="signout" title="Sair">
          ${Icons.voltar?.('sm') || '←'}
        </button>
      </div>
    </div>
  `;
}

function _roleLabel(tipo) {
  switch (tipo) {
    case 'administrador': return 'Administrador';
    case 'admin': return 'Administrador';
    case 'supervisor': return 'Supervisor';
    case 'funcionario': return 'Funcionário';
    case 'cliente': return 'Cliente';
    case 'super_admin': return 'SaaS Admin';
    default: return '—';
  }
}

// ─── Event delegation ────────────────────────────────────────────
function _bindListeners() {
  const sb = document.getElementById('app-sidebar');
  if (!sb) return;
  sb.addEventListener('click', (e) => {
    const target = e.target.closest('[data-action]');
    if (!target) return;
    const action = target.dataset.action;
    if (action === 'signout') return signOut();
    if (action === 'avatar') {
      const inp = document.createElement('input');
      inp.type = 'file'; inp.accept = 'image/*';
      inp.onchange = () => { const f = inp.files && inp.files[0]; if (f) _sbUploadAvatar(f); };
      inp.click();
      return;
    }
    if (action === 'toggle-group') {
      const id = target.dataset.group;
      const _shell = document.querySelector('.app-shell');
      if (_shell && _shell.classList.contains('sb-collapsed')) {
        const grp = NAV_GROUPS_ADMIN.find((x) => x.id === id);
        const first = grp?.items?.find((it) => !it.soon) || grp?.items?.[0];
        if (first?.page) navigate(first.page);
        return;
      }
      if (_openGroups.has(id)) _openGroups.delete(id);
      else _openGroups.add(id);
      _saveOpen(_openGroups);
      const groupEl = sb.querySelector(`[data-group="${id}"]`);
      groupEl?.classList.toggle('is-open', _openGroups.has(id));
      return;
    }
    if (action === 'nav') {
      if (target.dataset.soon) {
        showToast('Tela em migração para o Vite.', 'info', 2000);
        return;
      }
      const page = target.dataset.page;
      if (page) navigate(page);
    }
  });
}
